import { Controller } from "@hotwired/stimulus"
import { CutoutPlayer } from "./jane_cutout_player"

// A published duet: two twins on one stage (duet_stages/show). Play takes
// them through the approved lines in turn - the speaker talks, captioned,
// in the recording made when it was published; the other listens. It only
// plays what already exists: no model, no speech made here. Without WebGL,
// cut-outs or motion, the faces stay still and the lines still play.
const IDLE = ["rest", "rest_2", "rest_3", "idle"]
const TALK = ["talking", "talking_2", "talking_3"]
const LISTEN = ["listening", "listening_2", "idle"]

export default class extends Controller {
    static targets = ["figure", "caption", "play"]
    static values = { lines: Array, figures: Object }

    connect() {
        this.players = {}
        this.index = 0
        const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        if (still || !CutoutPlayer.supported()) return

        this.figureTargets.forEach((el) => {
            const side = el.dataset.side
            const clips = this.figuresValue[side]?.clips || {}
            if (!Object.keys(clips).length) return
            const canvas = el.querySelector("canvas")
            try {
                const player = new CutoutPlayer(canvas)
                canvas.style.aspectRatio = String(this.figuresValue[side].aspect || 0.6)
                player.video.addEventListener("playing", () => {
                    canvas.hidden = false
                    el.classList.add("is-alive")
                }, { once: true })
                this.players[side] = { player, clips }
                this.loop(side, IDLE)
            } catch (_error) {
                // A still face it is.
            }
        })
        this._onVisibility = () => Object.values(this.players).forEach(({ player }) => (document.hidden ? player.pause() : player.resume()))
        document.addEventListener("visibilitychange", this._onVisibility)
    }

    disconnect() {
        this.stop()
        document.removeEventListener("visibilitychange", this._onVisibility)
        Object.values(this.players).forEach(({ player }) => player.destroy())
        this.players = {}
    }

    toggle() {
        this.playing ? this.stop() : this.start()
    }

    start() {
        if (!this.linesValue.length) return
        if (this.index >= this.linesValue.length) this.index = 0
        this.playing = true
        this.setButton("pause", "Pause")
        this.next()
    }

    stop() {
        this.playing = false
        clearTimeout(this.timer)
        clearInterval(this.typing)
        if (this.audio) {
            this.audio.onended = this.audio.onplaying = this.audio.onerror = null
            this.audio.pause()
        }
        this.figureTargets.forEach((el) => el.classList.remove("is-speaking"))
        Object.keys(this.players).forEach((side) => this.loop(side, IDLE))
        this.setButton("play", this.index > 0 && this.index < this.linesValue.length ? "Resume" : "Play")
    }

    next() {
        if (!this.playing) return
        const line = this.linesValue[this.index]
        if (!line) return this.finish()

        const other = line.side === "inviter" ? "invitee" : "inviter"
        this.figureTargets.forEach((el) => el.classList.toggle("is-speaking", el.dataset.side === line.side))
        this.highlight(line.seq)
        this.loop(other, LISTEN)
        const name = this.figuresValue[line.side]?.name || ""
        const talk = (seconds) => {
            this.loop(line.side, TALK)
            this.type(`${name}: `, line.body, seconds)
        }
        const after = () => {
            this.index += 1
            this.loop(line.side, IDLE)
            this.timer = setTimeout(() => this.next(), 450)
        }
        const silent = () => {
            const seconds = Math.max(2, line.body.length / 15)
            talk(seconds)
            this.timer = setTimeout(after, seconds * 1000)
        }
        if (!line.audio) return silent()

        const audio = (this.audio ||= new Audio())
        audio.onplaying = () => talk(audio.duration)
        audio.onended = after
        audio.onerror = silent
        audio.src = line.audio
        audio.play().catch(silent)
    }

    finish() {
        this.playing = false
        this.index = 0
        this.figureTargets.forEach((el) => el.classList.remove("is-speaking"))
        this.highlight(null)
        this.setButton("arrow-counterclockwise", "Play again")
    }

    // Reveal the line word by word across the time it's spoken.
    type(prefix, text, seconds) {
        clearInterval(this.typing)
        const words = text.split(/\s+/)
        const step = Math.max(60, ((Number.isFinite(seconds) && seconds > 0 ? seconds : words.length / 2.5) * 1000) / words.length)
        let shown = 0
        this.typing = setInterval(() => {
            shown += 1
            this.captionTarget.textContent = prefix + words.slice(0, shown).join(" ")
            if (shown >= words.length) clearInterval(this.typing)
        }, step)
    }

    highlight(seq) {
        document.querySelectorAll("[data-duet-stage-seq]").forEach((li) => li.classList.toggle("is-current", String(seq) === li.dataset.duetStageSeq))
    }

    setButton(icon, label) {
        if (!this.hasPlayTarget) return
        this.playTarget.querySelector("i").className = `bi bi-${icon === "pause" ? "pause-fill" : icon === "play" ? "play-fill" : icon} me-1`
        this.playTarget.querySelector("span").textContent = label
    }

    loop(side, kinds) {
        const entry = this.players[side]
        if (!entry) return
        const available = kinds.filter((name) => entry.clips[name])
        const fresh = available.filter((name) => name !== entry.current)
        const choices = fresh.length ? fresh : available
        const kind = choices[Math.floor(Math.random() * choices.length)]
        if (!kind || entry.current === kind) return
        entry.current = kind
        entry.player.play(entry.clips[kind])
    }
}
